import React from 'react';
import { Phone, Clock, Activity, TrendingUp, TrendingDown, Minus } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import type { LangfuseTrace } from '@/types/traces';
import { processDashboardMetrics, calculateTrends } from '@/utils/traceAnalytics';

interface DashboardKPICardsProps {
  traces: LangfuseTrace[];
  isLoading?: boolean;
  showCosts?: boolean;
}

interface KPICardProps {
  title: string;
  value: string | number;
  subtitle: string;
  icon: React.ReactNode;
  trend?: {
    value: number;
    percentage: number;
    direction: 'up' | 'down' | 'stable';
  };
  badge?: {
    text: string;
    variant: 'default' | 'secondary' | 'destructive' | 'outline';
  };
}

const KPICard: React.FC<KPICardProps> = ({ title, value, subtitle, icon, trend, badge }) => {
  const getTrendIcon = () => {
    if (!trend) return null;
    
    switch (trend.direction) {
      case 'up':
        return <TrendingUp className="h-3 w-3 text-green-500" />;
      case 'down':
        return <TrendingDown className="h-3 w-3 text-red-500" />;
      default:
        return <Minus className="h-3 w-3 text-gray-500" />;
    }
  };

  const getTrendColor = () => {
    if (!trend) return '';
    switch (trend.direction) {
      case 'up':
        return 'text-green-600';
      case 'down':
        return 'text-red-600';
      default:
        return 'text-gray-600';
    }
  };

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <CardTitle className="text-sm font-medium">{title}</CardTitle>
        {icon}
      </CardHeader>
      <CardContent>
        <div className="flex items-center justify-between">
          <div>
            <div className="text-2xl font-bold">{value}</div>
            <p className="text-xs text-muted-foreground">{subtitle}</p>
          </div>
          <div className="flex flex-col items-end space-y-1">
            {badge && (
              <Badge variant={badge.variant} className="text-xs">
                {badge.text}
              </Badge>
            )}
            {trend && (
              <div className={`flex items-center space-x-1 text-xs ${getTrendColor()}`}>
                {getTrendIcon()}
                <span>{Math.abs(trend.percentage).toFixed(1)}%</span>
              </div>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export const DashboardKPICards: React.FC<DashboardKPICardsProps> = ({ 
  traces, 
  isLoading = false, 
  showCosts = false 
}) => {
  const metrics = React.useMemo(() => {
    if (!traces || traces.length === 0) {
      return {
        totalCalls: 0,
        callsToday: 0,
        averageDuration: 0,
        successRate: 0,
        uniqueUsers: 0,
        uniqueSessions: 0,
        totalCost: 0
      };
    }
    return processDashboardMetrics(traces);
  }, [traces]);

  const trends = React.useMemo(() => {
    if (!traces || traces.length === 0) return null;
    return calculateTrends(traces, 30);
  }, [traces]);

  const formatDuration = (seconds: number): string => {
    if (seconds < 60) {
      return `${seconds.toFixed(1)}s`;
    }
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}m ${remainingSeconds.toFixed(0)}s`;
  };

  const formatCost = (cost: number): string => {
    return `$${cost.toFixed(4)}`;
  };


  const cards: KPICardProps[] = [
    {
      title: 'Total de Llamadas',
      value: isLoading ? '...' : metrics.totalCalls.toLocaleString(),
      subtitle: 'Todas las llamadas',
      icon: <Phone className="h-4 w-4 text-muted-foreground" />,
      trend: trends?.calls && {
        value: trends.calls.value,
        percentage: trends.calls.percentage,
        direction: trends.calls.trend
      }
    },
    {
      title: 'Llamadas Hoy',
      value: isLoading ? '...' : metrics.callsToday.toLocaleString(),
      subtitle: 'Registradas hoy',
      icon: <Activity className="h-4 w-4 text-muted-foreground" />,
    },
    {
      title: 'Duración Promedio',
      value: isLoading ? '...' : formatDuration(metrics.averageDuration),
      subtitle: 'Tiempo por llamada',
      icon: <Clock className="h-4 w-4 text-muted-foreground" />,
      trend: trends?.avgDuration && {
        value: trends.avgDuration.value,
        percentage: trends.avgDuration.percentage,
        direction: trends.avgDuration.trend === 'up' ? 'down' : 'up' // Menor duración es mejor
      }
    },
  ];

  // Add cost card only for admin/developer roles
  if (showCosts) {
    cards.push({
      title: 'Costo Total',
      value: isLoading ? '...' : formatCost(metrics.totalCost || 0),
      subtitle: 'Costo acumulado',
      icon: <Activity className="h-4 w-4 text-muted-foreground" />,
    });
  }

  return (
    <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
      {cards.map((card, index) => (
        <KPICard key={index} {...card} />
      ))}
    </div>
  );
};

export default DashboardKPICards;